import {showToast} from '@/components/Toaster/Toaster';
import appConfig from '@/config/appConfig.typed';
import {AEM_ICC_UNREGISTRATION_TYPES} from '@/consts/aem/aemIccUnregistrationTypes';
import {ICC_PRODUCT_STATUS_TYPES} from '@/consts/icc/iccProductStatusTypes';
import {
    CHARGER_UNREGISTRATION_FAILED_NOTIFICATION_TEXT,
    DEVICE_UNREGISTERED_NOTIFICATION_TEXT,
    HOLDER_UNREGISTRATION_FAILED_NOTIFICATION_TEXT,
} from '@/consts/localization/localizationKeys';
import iccConsumerProducts from '@/services/icc/iccConsumerProducts';
import iccMarketService from '@/services/icc/iccMarketService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import log from '@/services/logger/log';
import httpStatusTypesService from '@/services/server/httpStatusTypesService';
import {selectGigyaToken} from '@/state/selectors/auth';
import {selectIsChargerRegistered, selectIsHolderRegistered} from '@/state/selectors/connectedProduct';
import {makeSelectConsumerProductToRegUnreg, makeSelectIsProductRegistered} from '@/state/selectors/consumer';
import {selectMwChargerData, selectMwHolderData} from '@/state/selectors/mw';
import {
    addConsumerProductToRegUnreg,
    removeConsumerProduct,
    removeConsumerProductFromRegUnreg,
} from '@/state/slices/consumerSlice';
import {dispatch, getState} from '@/state/store';

//isHolder default false to log false instead of undefined
const registerConsumerProduct = async ({isHolder = false, onSuccess, onError, onNotRegisteredHolder}) => {
    const state = getState();
    const charger = selectMwChargerData(state);
    const holder = selectMwHolderData(state);
    const device = isHolder ? holder : charger;
    const {codentify} = device || {};
    const {codentify: holderCodentify} = holder || {};

    try {
        log.info(`Start registration process. isHolder: ${isHolder}. codentify: ${codentify}.`);
        const gigyaToken = selectGigyaToken(getState());

        await iccMarketService.setConsumerProduct(codentify, gigyaToken);

        log.info(`End registration process. Success. isHolder: ${isHolder}. codentify: ${codentify}.`);
        await updateConsumerProducts([codentify, holderCodentify]);

        const isChargerRegistered = selectIsChargerRegistered(getState());

        if (!isChargerRegistered) {
            log.info(
                `End registration process. Failed after Success. isHolder: false. codentify: ${codentify}. error: Registered status of the device is not retrieved.`
            );
            onError?.({});
            return;
        }

        if (isHolder) return onSuccess?.();

        if (!holderCodentify) return onSuccess?.();

        //get status from new updated state
        const isHolderRegistered = selectIsHolderRegistered(getState());

        if (isHolderRegistered) return onSuccess?.();

        return onNotRegisteredHolder?.();
    } catch (error) {
        const iccError = getRegistrationFailedResponse(error);
        const logError = JSON.stringify(iccError);

        log.info(
            `End registration process. Failed. isHolder: ${isHolder}. codentify: ${codentify}. error: ${logError}.`
        );

        if (iccError.ignoreError) return;

        onError?.(iccError);
    }
};

const updateConsumerProducts = async (codentifyIds) => {
    const gigyaToken = selectGigyaToken(getState());

    await iccMarketService.getConsumerProducts(gigyaToken, true);
    await iccConsumerProducts.getDetailedProductsInfoByCodentifyIds(codentifyIds);
    await iccConsumerProducts.getProductsAssetsByProducts();
};

const getRegistrationFailedResponse = (error) => {
    const {status, response, message} = error || {};
    const messageValue = response?.data?.serviceError?.message?.value;
    const ignoreError =
        httpStatusTypesService.isNotAuthorized(status) || httpStatusTypesService.isServiceUnavailable(status);

    return {statusCode: status, messageValue: messageValue || message, ignoreError};
};

const unregisterConsumerProduct = async (device) => {
    const state = getState();
    const {codentify, holder} = device || {};
    const {codentify: holderCodentify} = holder || {};
    //already unreg in progress
    const productStatus = makeSelectConsumerProductToRegUnreg(codentify)(state);
    const holderProductStatus = makeSelectConsumerProductToRegUnreg(holderCodentify)(state);
    const isChargerUnregNeeded = codentify && !productStatus;
    const isHolderUnregNeeded = holderCodentify && !holderProductStatus;
    const unregResult = {};
    const localizedStrings = getLocalizedStrings();

    //first unreg charger then start holder unreg
    if (isChargerUnregNeeded) {
        const isChargerUnregistered = await iccUnregistration(device);

        if (isChargerUnregistered) {
            showToast(localizedStrings[DEVICE_UNREGISTERED_NOTIFICATION_TEXT]);
        } else {
            showToast(localizedStrings[CHARGER_UNREGISTRATION_FAILED_NOTIFICATION_TEXT]);
        }

        unregResult.isChargerUnregistered = isChargerUnregistered;
    }

    const shouldSendHolderRequest =
        isHolderUnregNeeded && (!isChargerUnregNeeded || (isChargerUnregNeeded && unregResult.isChargerUnregistered));

    if (shouldSendHolderRequest) {
        const isHolderRegistered = makeSelectIsProductRegistered(holderCodentify)(getState());

        if (!isHolderRegistered) {
            unregResult.isHolderUnregistered = true;
        } else {
            const isHolderUnregistered = await iccUnregistration(holder);

            if (isHolderUnregistered) {
                showToast(localizedStrings[DEVICE_UNREGISTERED_NOTIFICATION_TEXT]);
            } else {
                showToast(localizedStrings[HOLDER_UNREGISTRATION_FAILED_NOTIFICATION_TEXT]);
            }
        }
    }

    return unregResult;
};

const iccUnregistration = async (device) => {
    const {productId, codentify, isHolder = false} = device;

    log.info(`Start unregistration process. isHolder: ${isHolder}. codentify: ${codentify}.`);

    dispatch(addConsumerProductToRegUnreg({codentify, isHolder}));

    const onReturn = (isSuccess) => {
        dispatch(removeConsumerProductFromRegUnreg(codentify));

        return isSuccess;
    };

    const unregType = appConfig.getIccUnregistrationType();
    const isUseProductId = productId && unregType === AEM_ICC_UNREGISTRATION_TYPES.ALL;

    if (!isUseProductId && !codentify) {
        log.info(
            `End unregistration process. Failed. isHolder: ${isHolder}. codentify: ${codentify}. error: No codentify.`
        );
        return onReturn(false);
    }

    try {
        const productIdentify = isUseProductId ? {productId} : {codentifyId: codentify};
        const gigyaToken = selectGigyaToken(getState());

        await iccMarketService.removeConsumerProduct({data: productIdentify, gigyaToken});

        log.info(`End unregistration process. Success. isHolder: ${isHolder}. codentify: ${codentify}.`);
        await updateConsumerProducts();

        const isRegistered = makeSelectIsProductRegistered(codentify)(getState());

        if (isRegistered) {
            log.info(
                `End unregistration process. Failed after Success. isHolder: ${isHolder}. codentify: ${codentify}. error: Device is registered.`
            );
            return onReturn(false);
        }

        dispatch(removeConsumerProduct(codentify));

        return onReturn(true);
    } catch (error) {
        const iccError = getRegistrationFailedResponse(error);
        const logError = JSON.stringify(iccError);

        log.info(
            `End unregistration process. Failed. isHolder: ${isHolder}. codentify: ${codentify}. error: ${logError}.`
        );
    }

    return onReturn(false);
};

const isStatusRegistered = (status) => status === ICC_PRODUCT_STATUS_TYPES.REGISTERED;

const isStatusRegistering = (status) => status === ICC_PRODUCT_STATUS_TYPES.REGISTERING;

const isStatusUnregistered = (status) => status === undefined || status === ICC_PRODUCT_STATUS_TYPES.UNREGISTERED;

const isStatusUnregistering = (status) => status === ICC_PRODUCT_STATUS_TYPES.UNREGISTERING;

const isStatusRegisterFailed = (status) => status === ICC_PRODUCT_STATUS_TYPES.FAILED;

const proceedDeviceStatus = (statusFunction, status) => statusFunction(status);

const getIsRegistered = (status) => proceedDeviceStatus(isStatusRegistered, status);

const getIsRegistering = (status) => proceedDeviceStatus(isStatusRegistering, status);

const getIsUnregistered = (status) => proceedDeviceStatus(isStatusUnregistered, status);

const getIsRegisterFailed = (status) => proceedDeviceStatus(isStatusRegisterFailed, status);

const getIsUnregistering = (status) => proceedDeviceStatus(isStatusUnregistering, status);

export default {
    registerConsumerProduct,
    unregisterConsumerProduct,
    getIsRegistered,
    getIsRegistering,
    getIsUnregistered,
    getIsRegisterFailed,
    getIsUnregistering,
};
